import { characteristics } from '../../data/characteristics';
import { harmonicLevels } from '../../data/harmonicLevels';
import { characteristicsTR } from '../../data/tr/characteristics';
import { harmonicLevelsTR } from '../../data/tr/harmonicLevels';
import type { Score } from '../../types';
import type { HarmonicScore } from '../../types/harmonic';
import type { Locale } from './constants';

const RESOURCE_STATE_IDS = [9, 10, 11, 12];
const STABILISING_STATE_ID = 8;
const BLOCKER_STATE_IDS = [7, 6, 5, 4, 3, 2, 1];

export function buildAIPrompt(
  scores: Score[],
  harmonicScores: HarmonicScore[],
  instruction: string,
  locale: Locale = 'en'
): string {
  const isTR = locale === 'tr';
  const chars = isTR ? characteristicsTR : characteristics;
  const levels = isTR ? harmonicLevelsTR : harmonicLevels;

  const beingTraits = ['ambition', 'boldness', 'integrity', 'positiveOutlook'];
  const doingTraits = ['passion', 'sociability', 'communication', 'wisdom'];
  const havingTraits = ['learning', 'creativity', 'flexibility', 'visionaryThinking'];

  const formatTraitGroup = (traitIds: string[]) => scores
    .filter(s => traitIds.includes(s.characteristicId))
    .map(score => {
      const characteristic = chars.find(c => c.id === score.characteristicId);
      return `  ${characteristic?.name}: ${Math.round(score.normalized)}%`;
    }).join('\n');

  const getHarmonicByZone = (levelIds: number[]) => harmonicScores
    .filter(s => levelIds.includes(s.levelId))
    .sort((a, b) => b.normalized - a.normalized)
    .map(score => {
      const level = levels.find(l => l.id === score.levelId);
      return `  ${level?.name}: ${Math.round(score.normalized)}%`;
    }).join('\n');

  const resourceScores = harmonicScores.filter(s => RESOURCE_STATE_IDS.includes(s.levelId));
  const blockerScores = harmonicScores.filter(s => BLOCKER_STATE_IDS.includes(s.levelId));
  const stabilisingScore = harmonicScores.find(s => s.levelId === STABILISING_STATE_ID);

  const highestResource = resourceScores.reduce((prev, current) =>
    current.normalized > prev.normalized ? current : prev, resourceScores[0]);
  const highestBlocker = blockerScores.reduce((prev, current) =>
    current.normalized > prev.normalized ? current : prev, blockerScores[0]);

  const highestResourceLevel = levels.find(l => l.id === highestResource?.levelId);
  const highestBlockerLevel = levels.find(l => l.id === highestBlocker?.levelId);
  const stabilisingLevel = levels.find(l => l.id === STABILISING_STATE_ID);

  const labels = isTR ? {
    title: 'Human Potential OS™ Değerlendirme Verileri:',
    charProfile: 'KARAKTERİSTİK PROFİL',
    beingTraits: 'Varoluş Özellikleri (kimlik, içsel nitelikler, zihniyet, değerler):',
    doingTraits: 'Yapma Özellikleri (davranış, ifade, eylem, iletişim):',
    havingTraits: 'Sahip Olma Özellikleri (kapasiteler, kaynaklar, öğrenme, uyum, vizyon):',
    harmonicProfile: 'HARMONİK ÖLÇEK PROFİLİ',
    resourceStates: 'BÜYÜME / KAYNAK DURUMLARI (İlgi ve üzeri):',
    stabilisingState: 'DENGELEYİCİ DURUM:',
    blockerStates: 'ENGELLEYİCİ / MÜDAHALE DURUMLARI (Karşı Koyma ve aşağısı):',
    keyInterpretation: 'TEMEL HARMONİK YORUM',
    highestResource: 'En Yüksek Kaynak Durumu',
    highestBlocker: 'En Yüksek Engelleyici Durum',
    stabilising: 'Dengeleyici Durum',
    remember: 'Unut: Yüksek durumlar kullanılabilir potansiyeli gösterir. Düşük durumlar bu potansiyeli neyin engelleyebileceğini gösterir. En yüksek engelleyici genellikle kişinin en güçlü özelliklerinin gösterdiği potansiyeli neden tam yaşamadığını açıklar.',
    instruction: 'TALİMAT',
  } : {
    title: 'Human Potential OS™ Assessment Data:',
    charProfile: 'CHARACTERISTIC PROFILE',
    beingTraits: 'Being Traits (identity, inner qualities, mindset, values):',
    doingTraits: 'Doing Traits (behaviour, expression, action, communication):',
    havingTraits: 'Having Traits (capacities, resources, learning, adaptability, vision):',
    harmonicProfile: 'HARMONIC SCALE PROFILE',
    resourceStates: 'GROWTH / RESOURCE STATES (Interest and above):',
    stabilisingState: 'STABILISING STATE:',
    blockerStates: 'BLOCKER / INTERFERENCE STATES (Antagonism and below):',
    keyInterpretation: 'KEY HARMONIC INTERPRETATION',
    highestResource: 'Highest Resource State',
    highestBlocker: 'Highest Blocker State',
    stabilising: 'Stabilising State',
    remember: 'Remember: Higher states show available potential. Lower states show what may block that potential. The highest blocker often explains why the person is not fully living the potential shown by their strongest traits.',
    instruction: 'INSTRUCTION',
  };

  return `
${labels.title}

═══════════════════════════════════════
${labels.charProfile}
═══════════════════════════════════════

${labels.beingTraits}
${formatTraitGroup(beingTraits)}

${labels.doingTraits}
${formatTraitGroup(doingTraits)}

${labels.havingTraits}
${formatTraitGroup(havingTraits)}

═══════════════════════════════════════
${labels.harmonicProfile}
═══════════════════════════════════════

${labels.resourceStates}
${getHarmonicByZone(RESOURCE_STATE_IDS)}

${labels.stabilisingState}
  ${stabilisingLevel?.name}: ${stabilisingScore ? Math.round(stabilisingScore.normalized) : 0}%

${labels.blockerStates}
${getHarmonicByZone(BLOCKER_STATE_IDS)}

═══════════════════════════════════════
${labels.keyInterpretation}
═══════════════════════════════════════

${labels.highestResource}: ${highestResourceLevel?.name} — ${highestResource ? Math.round(highestResource.normalized) : 0}%
${labels.highestBlocker}: ${highestBlockerLevel?.name} — ${highestBlocker ? Math.round(highestBlocker.normalized) : 0}%
${labels.stabilising}: ${stabilisingLevel?.name} — ${stabilisingScore ? Math.round(stabilisingScore.normalized) : 0}%

${labels.remember}

═══════════════════════════════════════
${labels.instruction}
═══════════════════════════════════════

${instruction}
`;
}
